import React, { useState, useEffect, useRef } from 'react';
import PageApp from '../../../Page';
import ModalEdit from './Edit';
import {
  HomeOutlined,
  MailOutlined,
  StarOutlined,
  StarFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import {
  Table,
  Button,
  message,
  Space,
  Tag,
  Modal,
  Select,
  Tooltip,
  Popconfirm,
  Empty,
} from 'antd';
import {
  listTemplates,
  setDefaultTemplate,
  deactivateTemplate,
} from '../../../../apis/TemplateEmailApi';

const { Option } = Select;
const { confirm } = Modal;

const routes = [
  {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <MailOutlined />
        <span>Plantillas de Email</span>
      </>
    ),
  },
];

const tiposTemplate = [
  { value: 'nomina_recibo', label: 'Recibo de Nómina' },
  { value: 'bienvenida', label: 'Bienvenida' },
  { value: 'despedida', label: 'Despedida' },
  { value: 'notificacion', label: 'Notificación' },
  { value: 'otro', label: 'Otro' },
];

const idiomasDisponibles = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'Inglés' },
];

const PlantillaEmailPage = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [objectSelected, setObjectSelected] = useState(null);
  const [filterTipo, setFilterTipo] = useState(null);
  const [filterIdioma, setFilterIdioma] = useState(null);

  const effectRan = useRef(false);
  useEffect(() => {
    if (!effectRan.current) {
      (async () => {
        await refreshData();
      })();
    }
    return () => effectRan.current = true;
  }, [filterTipo, filterIdioma]);

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await listTemplates(filterTipo, filterIdioma);
      if (response?.ok && Array.isArray(response.payload)) {
        setTemplates(response.payload);
      } else if (response?.ok && response.payload) {
        setTemplates(Array.isArray(response.payload) ? response.payload : [response.payload]);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
      message.error('Error al cargar plantillas');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (object) => {
    setOpenEdit(true);
    setObjectSelected(object);
  };

  const btnClick = () => {
    setOpenEdit(true);
    setObjectSelected(null);
  };

  const onHandlerFinish = async (refresh) => {
    setOpenEdit(false);
    if (refresh) {
      await refreshData();
    }
  };

  const handleSetDefault = async (record) => {
    setLoading(true);
    try {
      const response = await setDefaultTemplate(record.id);
      if (response?.ok) {
        message.success('¡Plantilla marcada como predeterminada!');
        await refreshData();
      } else {
        message.error(response?.message || 'Error al marcar como predeterminada');
      }
    } catch (error) {
      message.error('Error al marcar como predeterminada');
    } finally {
      setLoading(false);
    }
  };

  const deactivateConfirm = async (item) => {
    confirm({
      title: '¿Confirmas que deseas desactivar esta plantilla?',
      icon: <ExclamationCircleFilled />,
      content: 'La plantilla dejará de estar disponible',
      onOk: () => handleDeactivate(item),
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleDeactivate = async (record) => {
    setLoading(true);
    try {
      const response = await deactivateTemplate(record.id);
      if (response?.ok) {
        message.success('¡Plantilla desactivada!');
        await refreshData();
      } else {
        message.error(response?.message || 'Error al desactivar plantilla');
      }
    } catch (error) {
      message.error('Error al desactivar plantilla');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 180,
      sorter: (a, b) => a.codigo.localeCompare(b.codigo),
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 150,
      render: (tipo) => {
        const config = tiposTemplate.find((t) => t.value === tipo);
        return <Tag color="blue">{config?.label || tipo}</Tag>;
      },
    },
    {
      title: 'Idioma',
      dataIndex: 'idioma',
      key: 'idioma',
      width: 100,
      align: 'center',
      render: (idioma) => {
        const config = idiomasDisponibles.find((i) => i.value === idioma);
        return <Tag>{config?.label || idioma}</Tag>;
      },
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 140,
      align: 'left',
      render: (_, record) => (
        <Space>
          <Tag color={record.is_active ? 'success' : 'error'}>
            {record.is_active ? 'Activa' : 'Inactiva'}
          </Tag>
          {record.is_default && (
            <Tooltip title="Predeterminada">
              <StarFilled style={{ color: '#faad14', fontSize: 16 }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      key: 'acciones',
      width: 200,
      align: 'left',
      render: (_, record) => (
        <Space>
          <Button 
            type='primary' 
            size='small' 
            onClick={() => openDetails(record)}
          >
            Editar
          </Button>
          {record.is_active && (
            <Button 
              type='primary' 
              size='small' 
              danger 
              onClick={() => deactivateConfirm(record)}
            >
              Desactivar
            </Button>
          )}
          {!record.is_default && record.is_active && (
            <Popconfirm
              title="¿Marcar como predeterminada?"
              onConfirm={() => handleSetDefault(record)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title="Marcar como predeterminada">
                <Button type="default" icon={<StarOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageApp 
      routes={routes} 
      title="Plantillas de Email"
      extraCard={[
        <Button type="primary" onClick={btnClick}>
          Agregar Plantilla
        </Button>
      ]}
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Filtrar por tipo"
          allowClear
          style={{ width: 200 }}
          value={filterTipo}
          onChange={setFilterTipo}
        >
          {tiposTemplate.map((t) => (
            <Option key={t.value} value={t.value}>
              {t.label}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Filtrar por idioma"
          allowClear
          style={{ width: 150 }}
          value={filterIdioma}
          onChange={setFilterIdioma}
        >
          {idiomasDisponibles.map((i) => (
            <Option key={i.value} value={i.value}>
              {i.label}
            </Option>
          ))}
        </Select>
      </Space>

      {loading ? (
        <p>Cargando...</p>
      ) : templates.length === 0 ? (
        <Empty description="No hay plantillas de email registradas" />
      ) : (
        <Table
          rowKey="id"
          dataSource={templates}
          columns={columns}
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} plantillas`
          }}
        />
      )}

      {openEdit && (
        <ModalEdit
          title={objectSelected ? "Editar Plantilla de Email" : "Crear Plantilla de Email"}
          id={objectSelected?.id}
          open={openEdit}
          finish={onHandlerFinish}
        />
      )}
    </PageApp>
  );
};

export default PlantillaEmailPage;
